import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { FoodFilterColumn } from '@app/purchased-goods/food-and-drink/filters';
import { FoodFilterOption } from '@app/sold-goods/filters';
import {
  FoodActivitiesList,
  FoodOperationActivitiesList,
  OperationReportTypeEnum,
  PurchasedGoodsFoodListReviewType,
  SoldGoodModelledAsEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { ActivityCell } from '@shared/components/data-table/cells/activity-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';

interface FoodReviewColumnsProps {
  reportType: OperationReportTypeEnum;
  onEdit?: (rowId: FoodActivitiesList['uuid']) => void;
  onConfirm?: (rowId: FoodActivitiesList['uuid']) => void;
  updatingRowId: FoodActivitiesList['uuid'] | null;
  filters: FilterOptions<FoodFilterOption[]>;
}

export function useFoodReviewColumns({
  reportType,
  onEdit,
  onConfirm,
  updatingRowId,
  filters,
}: FoodReviewColumnsProps): ColumnDef<
  FoodActivitiesList | FoodOperationActivitiesList
>[] {
  return [
    {
      id: 'id',
      accessorKey: 'id',
      enableSorting: false,
      enableHiding: false,
    },

    {
      id: 'uploadedData',
      accessorKey: 'uploadedData',
      header: ({ column }) => (
        <div className="flex justify-center">
          <HeaderCell column={column} name="Your input data" />
        </div>
      ),
      enableSorting: false,
      enableColumnFilter: true,
      columns: [
        {
          id: 'food',
          accessorKey: 'food',
          accessorFn: (row) => row.cleanupResult,
          header: ({ column }) => <HeaderCell column={column} name="Name" />,
          cell: ({ getValue, row }) => {
            const remoteId = row.original.remoteId;
            const food = getValue<FoodActivitiesList['cleanupResult']>();
            return (
              <ActivityCell
                className="w-full min-w-52"
                name={food.name}
                remoteId={remoteId!}
              />
            );
          },
          enableSorting: false,
          enableColumnFilter: true,
          meta: {
            label: 'Name',
            variant: 'text',
            sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
          },
        },
        {
          id: 'inputUnit',
          accessorKey: 'inputUnit',
          accessorFn: (row) => row.cleanupResult?.inputUnit,
          header: ({ column }) => <HeaderCell column={column} name="Unit" />,
          cell: ({ getValue }) => {
            return <TextCell text={getValue<string>()} />;
          },
          enableSorting: false,
          size: 30,
        },
      ],
    },

    {
      id: 'remoteId',
      accessorKey: 'remoteId',
      accessorFn: (row) => row.cleanupResult?.remoteId,
      header: ({ column }) => (
        <HeaderCell column={column} name="ID" className="text-nowrap" />
      ),
      cell: ({ getValue }) => {
        return <Text>{getValue<string>()}</Text>;
      },

      enableSorting: false,
    },
    {
      id: 'processedData',
      accessorKey: 'processedData',
      header: ({ column }) => (
        <div className="flex justify-center">
          <HeaderCell
            column={column}
            name="Our processed data"
            className="text-nowrap text-center"
          />
        </div>
      ),
      enableSorting: false,
      columns: [
        {
          id: 'matchedFood',
          accessorKey: 'matchedFood',
          accessorFn: (row) =>
            reportType === 'purchase'
              ? (row.purchasedGoodInfo as FoodActivitiesList['purchasedGoodInfo'])
              : (row.soldGoodInfo as FoodOperationActivitiesList['soldGoodInfo']),
          enableSorting: false,
          header: ({ column }) => (
            <HeaderCell column={column} name="Matched food" />
          ),
          cell: ({ getValue, row }) => {
            const entityInfo = getValue();

            const modelledAs = row.original.modelledAs;
            const isValidFood = !row.original.cleanupResult?.foodNeedsReview;

            let matchedFood = '-';

            switch (modelledAs) {
              case SoldGoodModelledAsEnum.basic_food:
                matchedFood = entityInfo.basicFoodName;
                break;
              case SoldGoodModelledAsEnum.product:
                matchedFood = entityInfo.productName;
                return (
                  <SimpleTooltip
                    trigger={
                      <span className="flex max-w-80 items-center gap-1">
                        <Icons.salad className="size-4 shrink-0 text-gray-500" />
                        <Text className="truncate">{matchedFood}</Text>
                      </span>
                    }
                    content={matchedFood}
                  />
                );
              case SoldGoodModelledAsEnum.purchased_good:
                matchedFood = entityInfo.purchasedGoodName;
                return (
                  <span className="flex max-w-80 items-center gap-1">
                    <Icons.shoppingCart className="size-4 shrink-0 text-gray-500" />
                    <Text className="truncate" includeHover>
                      {matchedFood}
                    </Text>
                  </span>
                );
            }
            return (
              <Text
                className={cn('', {
                  'text-orange-600 font-semibold': !isValidFood,
                })}
              >
                {matchedFood}
              </Text>
            );
          },
          enableColumnFilter: true,
        },
        {
          id: 'packWeight',
          accessorKey: 'packWeight',
          accessorFn: (row) =>
            reportType === 'purchase'
              ? row.purchasedGoodInfo.itemMassOrVolume
              : row.soldGoodInfo.itemMassOrVolume,
          header: ({ column }) => (
            <HeaderCell column={column} name="Pack weight" numberColumn />
          ),
          cell: ({ row, getValue }) => {
            const isValidWeight =
              !row.original.cleanupResult?.weightNeedsReview;

            const unit =
              reportType === 'purchase'
                ? row.original.purchasedGoodInfo.itemUnit
                : row.original.soldGoodInfo.itemUnit;

            const itemAmount = getValue<number>();
            const itemsPerPack =
              reportType === 'purchase'
                ? row.original.purchasedGoodInfo.itemsPerPack
                : row.original.soldGoodInfo.itemsPerPack;

            const text = itemsPerPack
              ? `${itemAmount * itemsPerPack!} ${unit}`
              : '-';

            if (itemsPerPack) {
              return (
                <Text
                  className={cn('float-right', {
                    'text-orange-600 font-semibold': !isValidWeight,
                  })}
                  includeHover
                  hoverText={`${itemsPerPack} x ${itemAmount} ${unit}`}
                >
                  {text}
                </Text>
              );
            }
            return (
              <Text
                className={cn('float-right', {
                  'text-orange-600 font-semibold': !isValidWeight,
                })}
                includeHover
                hoverText={`Item imported using total weight.`}
              >
                {'-'}
              </Text>
            );
          },
          meta: {
            label: 'Pack weight',
            sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' }, // <-- Use an object, not a boolean
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'review',
      accessorKey: 'review',
      header: ({ column }) => (
        <div className="flex justify-center">
          <HeaderCell column={column} name="Review" className="" />
        </div>
      ),
      enableSorting: false,
      columns: [
        {
          id: 'confirm',
          accessorKey: 'confirm',
          size: 10,
          header: ({ column }) => (
            <HeaderCell
              column={column}
              name="Confirm"
              className="justify-center text-nowrap"
            />
          ),
          cell: ({ row }) => (
            <div className="flex">
              <Button
                variant="ghost"
                size="icon"
                onClick={
                  onConfirm ? () => onConfirm(row.original.uuid) : undefined
                }
                loading={updatingRowId === row.original.uuid}
                className="mx-auto text-secondary-500"
                icon={<Icons.check size={12} />}
              />
            </div>
          ),
          enableSorting: false,
        },
        {
          id: 'edit',
          accessorKey: 'edit',
          size: 10,
          header: ({ column }) => (
            <HeaderCell
              column={column}
              name="Edit"
              className="justify-center text-nowrap"
            />
          ),
          cell: ({ row }) => (
            <div className="flex">
              <Button
                variant="ghost"
                size="icon"
                className="mx-auto"
                disabled={updatingRowId !== null}
                onClick={onEdit ? () => onEdit(row.original.uuid) : undefined}
                icon={<Icons.pencil />}
              />
            </div>
          ),
          enableSorting: false,
        },
      ],
    },
    {
      id: 'reviewType',
      accessorKey: 'reviewType',
      enableColumnFilter: true,
      enableHiding: true,
      enableSorting: false,
      meta: {
        label: 'Review Type',
        variant: 'select',
        options: [
          { label: 'Food', value: PurchasedGoodsFoodListReviewType.food },
          { label: 'Weight', value: PurchasedGoodsFoodListReviewType.weight },
        ],
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'emissions',
      accessorKey: 'emissions',
      enableSorting: true,
      enableHiding: true,
      meta: {
        label: 'Emissions',
        sorting: { ascLabel: 'Low-High', descLabel: 'High-Low' },
      },
    },
    {
      id: FoodFilterColumn.ActivitiesImport,
      accessorKey: FoodFilterColumn.ActivitiesImport,
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: 'Import',
        variant: 'multiSelect',
        options: filters.activitiesImport,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Created,
      accessorKey: FoodFilterColumn.Created,
      enableColumnFilter: false,
      enableSorting: true,
      enableHiding: true,
      meta: {
        label: 'Created',
        sorting: { ascLabel: 'Oldest-Newest', descLabel: 'Newest-Oldest' },
      },
    },
  ];
}
